import { useEffect, useState } from "react";
import axios from "axios";
import Seo from "@/components/Seo";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const TOKEN_KEY = "natant_admin_token";

function formatApiErrorDetail(detail) {
        if (detail == null) return "Something went wrong. Please try again.";
        if (typeof detail === "string") return detail;
        if (Array.isArray(detail))
                return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
        if (detail && typeof detail.msg === "string") return detail.msg;
        return String(detail);
}

function LoginCard({ onLogin }) {
        const [email, setEmail] = useState("");
        const [password, setPassword] = useState("");
        const [error, setError] = useState("");
        const [busy, setBusy] = useState(false);

        const submit = async (e) => {
                e.preventDefault();
                setBusy(true);
                setError("");
                try {
                        const { data } = await axios.post(`${API}/admin/login`, { email, password });
                        localStorage.setItem(TOKEN_KEY, data.token);
                        onLogin(data.token);
                } catch (err) {
                        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
                } finally {
                        setBusy(false);
                }
        };

        return (
                <div className="min-h-screen flex items-center justify-center px-6">
                        <form
                                onSubmit={submit}
                                data-testid="admin-login-form"
                                className="w-full max-w-md border border-[var(--border-line)] rounded-2xl bg-[var(--surface)] p-8"
                        >
                                <div className="step-eyebrow mb-3">Natant AI Labs</div>
                                <h1 className="h2x text-2xl mb-8">Applications sign-in</h1>
                                <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--bone-dim)] mb-2" htmlFor="admin-email">
                                        Email
                                </label>
                                <input
                                        id="admin-email"
                                        data-testid="admin-email-input"
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full mb-5 px-4 py-3 rounded-xl border border-[var(--border-line)] bg-transparent outline-none focus:border-[var(--accent-green)]"
                                        autoComplete="username"
                                />
                                <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--bone-dim)] mb-2" htmlFor="admin-password">
                                        Password
                                </label>
                                <input
                                        id="admin-password"
                                        data-testid="admin-password-input"
                                        type="password"
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full mb-6 px-4 py-3 rounded-xl border border-[var(--border-line)] bg-transparent outline-none focus:border-[var(--accent-green)]"
                                        autoComplete="current-password"
                                />
                                {error && (
                                        <div data-testid="admin-login-error" className="mb-5 text-sm acc">
                                                {error}
                                        </div>
                                )}
                                <button
                                        type="submit"
                                        data-testid="admin-login-submit"
                                        disabled={busy}
                                        className="pill-btn w-full justify-center"
                                        style={{ background: "var(--accent-green)", borderColor: "var(--accent-green)", color: "#fff" }}
                                >
                                        {busy ? "Signing in…" : "Sign in"}
                                </button>
                        </form>
                </div>
        );
}

export default function AdminPage() {
        const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
        const [data, setData] = useState(null);
        const [error, setError] = useState("");
        const [statusBusy, setStatusBusy] = useState("");

        const setStatus = async (id, status) => {
                setStatusBusy(id + status);
                try {
                        await axios.patch(
                                `${API}/admin/applications/${id}/status`,
                                { status },
                                { headers: { Authorization: `Bearer ${token}` } }
                        );
                        setData((d) => ({
                                ...d,
                                applications: d.applications.map((a) => (a.id === id ? { ...a, status } : a)),
                        }));
                } catch (err) {
                        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
                } finally {
                        setStatusBusy("");
                }
        };

        useEffect(() => {
                if (!token) return;
                let cancelled = false;
                axios
                        .get(`${API}/admin/applications`, { headers: { Authorization: `Bearer ${token}` } })
                        .then(({ data }) => {
                                if (!cancelled) setData(data);
                        })
                        .catch((err) => {
                                if (cancelled) return;
                                if (err.response?.status === 401) {
                                        localStorage.removeItem(TOKEN_KEY);
                                        setToken("");
                                } else {
                                        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
                                }
                        });
                return () => {
                        cancelled = true;
                };
        }, [token]);

        if (!token)
                return (
                        <>
                                <Seo title="Admin — Natant" description="Internal applications view." path="/admin" noindex />
                                <LoginCard onLogin={setToken} />
                        </>
                );

        const logout = () => {
                localStorage.removeItem(TOKEN_KEY);
                setToken("");
                setData(null);
        };

        return (
                <div className="min-h-screen py-16 md:py-20">
                        <Seo title="Admin — Natant" description="Internal applications view." path="/admin" noindex />
                        <div className="max-container">
                                <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
                                        <div>
                                                <div className="step-eyebrow mb-3">Natant AI Labs · Admin</div>
                                                <h1 className="h2x text-3xl md:text-4xl">
                                                        Cohort 1 <span className="acc">applications</span>
                                                </h1>
                                        </div>
                                        <button
                                                onClick={logout}
                                                data-testid="admin-logout-button"
                                                className="pill-btn pill-btn--ghost text-sm"
                                        >
                                                Sign out
                                        </button>
                                </div>

                                {error && (
                                        <div data-testid="admin-list-error" className="mb-6 text-sm acc">
                                                {error}
                                        </div>
                                )}
                                {!data && !error && <div className="text-[var(--bone-dim)]">Loading…</div>}
                                {data && data.applications.length === 0 && (
                                        <div data-testid="admin-empty" className="text-[var(--bone-dim)]">
                                                No applications yet.
                                        </div>
                                )}

                                <div data-testid="admin-applications-list" className="border-t border-l border-[var(--border-line)]">
                                        {data?.applications.map((a) => (
                                                <article
                                                        key={a.id}
                                                        data-testid="admin-application-row"
                                                        className="border-r border-b border-[var(--border-line)] p-6 md:p-7 bg-[var(--surface)]"
                                                >
                                                        <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
                                                                <h2 className="font-display font-bold text-xl tracking-tight">{a.organisation}</h2>
                                                                <div className="flex items-center gap-4">
                                                                        <div
                                                                                className="flex rounded-full border border-[var(--border-line)] overflow-hidden"
                                                                                data-testid={`admin-status-${a.id}`}
                                                                        >
                                                                                {["new", "contacted", "replied"].map((st) => {
                                                                                        const active = (a.status || "new") === st;
                                                                                        return (
                                                                                                <button
                                                                                                        key={st}
                                                                                                        type="button"
                                                                                                        data-testid={`admin-mark-${st}`}
                                                                                                        disabled={statusBusy === a.id + st}
                                                                                                        onClick={() => setStatus(a.id, st)}
                                                                                                        className={`px-3.5 py-1.5 font-mono text-[10px] tracking-[0.14em] uppercase transition-colors ${
                                                                                                                active
                                                                                                                        ? "text-white"
                                                                                                                        : "text-[var(--bone-dim)] hover:text-[var(--bone)]"
                                                                                                        }`}
                                                                                                        style={active ? { background: "var(--accent-green)" } : undefined}
                                                                                                >
                                                                                                        {st}
                                                                                                </button>
                                                                                        );
                                                                                })}
                                                                        </div>
                                                                        <time className="font-mono text-[11px] tracking-[0.12em] text-[var(--bone-dim)]">
                                                                                {new Date(a.submitted_at).toLocaleString()}
                                                                        </time>
                                                                </div>
                                                        </div>
                                                        <div className="flex flex-wrap items-center gap-3 mb-3">
                                                                <a
                                                                        href={`mailto:${a.email}?subject=Re: Your Cohort 1 application — ${encodeURIComponent(a.organisation)}`}
                                                                        data-testid="admin-reply-link"
                                                                        className="text-sm acc font-medium"
                                                                >
                                                                        {a.email} →
                                                                </a>
                                                                {a.company_url && (
                                                                        <span className="font-mono text-[11px] text-[var(--bone-dim)]">{a.company_url}</span>
                                                                )}
                                                        </div>
                                                        <p className="text-sm text-[var(--bone-dim)] leading-relaxed mb-3 max-w-[90ch]">{a.description}</p>
                                                        {(a.accepted_patterns?.length > 0 || a.tools?.length > 0) && (
                                                                <div className="flex flex-wrap gap-2">
                                                                        {a.accepted_patterns.map((p) => (
                                                                                <span
                                                                                        key={p}
                                                                                        className="font-mono text-[10px] tracking-[0.14em] uppercase px-2.5 py-1 rounded-full border border-[var(--accent-green)] acc"
                                                                                >
                                                                                        {p.replace(/-/g, " ")}
                                                                                </span>
                                                                        ))}
                                                                        {a.tools.map((t) => (
                                                                                <span
                                                                                        key={t}
                                                                                        className="font-mono text-[10px] tracking-[0.14em] uppercase px-2.5 py-1 rounded-full border border-[var(--border-strong)] text-[var(--bone-dim)]"
                                                                                >
                                                                                        {t}
                                                                                </span>
                                                                        ))}
                                                                </div>
                                                        )}
                                                </article>
                                        ))}
                                </div>
                        </div>
                </div>
        );
}
